import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clapperboard, Sparkles, X, Pencil } from 'lucide-react';
import { pollSubtitleJob } from '../../utils/subtitles';
import SubtitleEditor from './SubtitleEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContentVideoSubtitles = () => {
  const [videos, setVideos] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null); // { video, lang } | null

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/content-videos`, { withCredentials: true });
      setVideos(res.data.videos || []);
    } catch (err) {
      // Section stays hidden if fetch fails
    }
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleGenerate = async (video) => {
    setGenerating(video.key);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/content-videos/${video.key}/generate-subtitles`,
        {},
        { withCredentials: true }
      );
      const job = await pollSubtitleJob('content', video.key);
      if (job.status === 'error') {
        toast.error(job.error || 'Subtitle generation failed');
      } else {
        const result = job.result || {};
        const generated = Object.keys(result.generated || {});
        const failed = Object.keys(result.failed || {});
        if (generated.length) {
          toast.success(`${video.label}: subtitles generated for ${generated.map(l => l.toUpperCase()).join(', ')}`);
        } else {
          toast.info(`${video.label}: all registered languages already have subtitles`);
        }
        if (failed.length) {
          toast.error(`Failed languages: ${failed.map(l => l.toUpperCase()).join(', ')}`);
        }
      }
      fetchVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Subtitle generation failed');
    } finally {
      setGenerating(null);
    }
  };

  const handleDelete = async (video, lang) => {
    const confirmMsg = `Delete the ${lang.toUpperCase()} subtitle for "${video.label}"?\n\n`
      + `This removes it from the site immediately and deletes the underlying VTT file. `
      + `You'll be able to regenerate it fresh.`;
    if (!window.confirm(confirmMsg)) return;
    const key = `${video.key}:${lang}`;
    setDeleting(key);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/content-videos/${video.key}/subtitles/${lang}`,
        { withCredentials: true }
      );
      toast.success(`${lang.toUpperCase()} subtitle removed from ${video.label}`);
      fetchVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to delete subtitle');
    } finally {
      setDeleting(null);
    }
  };

  if (videos.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="content-video-subtitles">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        <Clapperboard className="w-4 h-4 text-slate-500" />
        <h3 className="font-medium text-slate-700">Site Video Subtitles (AI)</h3>
      </div>
      <div className="divide-y divide-slate-100">
        {videos.map((video) => (
          <div key={video.key} className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`content-video-${video.key}`}>
            <div className="min-w-0">
              <p className="font-medium text-slate-900 truncate">{video.label}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {Object.entries(video.subtitles || {}).filter(([, url]) => url).map(([lang]) => {
                  const key = `${video.key}:${lang}`;
                  const isDeleting = deleting === key;
                  return (
                    <span
                      key={lang}
                      className="inline-flex items-center gap-0.5 text-xs bg-slate-200 text-slate-600 pl-1.5 pr-0.5 py-0.5 rounded uppercase"
                    >
                      {lang}
                      <button
                        onClick={() => setEditing({ video, lang })}
                        disabled={isDeleting || generating !== null}
                        data-testid={`edit-content-subtitle-${video.key}-${lang}`}
                        title={`Edit ${lang.toUpperCase()} subtitle cues`}
                        className="rounded p-0.5 text-slate-400 hover:text-slate-800 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(video, lang)}
                        disabled={isDeleting || generating !== null}
                        data-testid={`delete-content-subtitle-${video.key}-${lang}`}
                        title={`Delete ${lang.toUpperCase()} subtitle`}
                        className="rounded p-0.5 text-slate-400 hover:text-red-600 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
                {Object.values(video.subtitles || {}).filter(Boolean).length === 0 && (
                  <span className="text-xs text-slate-400">No subtitles yet</span>
                )}
              </div>
            </div>
            <button
              onClick={() => handleGenerate(video)}
              disabled={generating !== null || !video.videoUrl}
              data-testid={`generate-content-subtitles-${video.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                generating === video.key
                  ? 'bg-indigo-100 text-indigo-500 cursor-wait'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              {generating === video.key ? 'Generating…' : 'Generate Subtitles'}
            </button>
          </div>
        ))}
      </div>
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
        <p className="text-xs text-slate-500">
          Transcribes English subtitles and translates to every registered language. Existing subtitles are never replaced.
        </p>
      </div>
      {editing && (
        <SubtitleEditor
          title={`${editing.video.label} — ${editing.lang.toUpperCase()}`}
          getUrl={`${BACKEND_URL}/api/admin/content-videos/${editing.video.key}/subtitles/${editing.lang}/cues`}
          putUrl={`${BACKEND_URL}/api/admin/content-videos/${editing.video.key}/subtitles/${editing.lang}/cues`}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchVideos(); }}
        />
      )}
    </div>
  );
};

export default ContentVideoSubtitles;
